//@flow
import React, {useMemo, useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {
  Avatar,
  Card,
  CardActionArea,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import {Edit, AddCircleTwoTone} from '@material-ui/icons';

import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import CustomChip from '../../../../../elements/CustomChip';
import {getImageUrl} from '../../../../../utils/image_provider';

import AlertActivePromotion from '../AlertActivePromotion/AlertActivePromotion';
import {commaFormat} from '../../../../../utils/number';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';
import {
  getCustomDomain,
  useIsFeatureInTier,
} from '../../../../../utils/component';

import type {SinglePromotion, Currency, Store} from '../../../../../entities';

type Props = {
  store?: Store,
  shouldCreatePromotions?: boolean,
  promotion?: SinglePromotion,
  cleanPromotion: () => void,
  type?: 'CREATE' | '',
  handleClick?: () => void,
  currency?: Currency,
  updatePromotion?: ({
    promotionRequest: {enabled: boolean},
    promotionId: string,
  }) => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    position: 'relative',
    height: '100%',
    width: '100%',
    margin: '0 auto',
    padding: '.5rem',
    background: '#FCFCFD',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '8px',

    [theme.breakpoints.up('md')]: {
      padding: '.75rem',
    },
  },
  cardRoot: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    position: 'relative',
    height: '100%',
    width: '100%',
    boxShadow: 'unset',
    margin: '0 auto',
  },
  cardActionArea: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    height: '100%',
    borderRadius: '12px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 216,
    height: '100%',
    width: '100%',
    border: '2px dashed #B8B8C7',
    opacity: 0.6,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 20,
    borderRadius: '12px',
    '&:hover': {
      opacity: 1,
    },
    transition: 'opacity 0.2s',
  },
  titleWrap: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '22px',
    color: colors.grey800,
    margin: '0',
  },
  paragraph: {
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '22px',
    color: colors.grey700,
    margin: '0',
    maxWidth: '130px',
    textAlign: 'center',
  },
  chip: {
    background: '#00B382',
    borderRadius: '12px',
    maxHeight: '24px',
    color: 'white',
    padding: '2px 8px',
  },
  chipExpired: {
    background: '#E04763',
  },
  promotionInformation: {
    padding: '.25rem',
    margin: '0',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    alignItems: 'flex-start',
    marginLeft: '.75rem',
  },
  description: {
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.005em',
    color: colors.grey800,
  },
  icon: {
    background: '#EEE5FF',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    marginRight: '4px',
    padding: '4px',
  },
  buttonWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    marginTop: theme.spacing(1),
  },
  switchContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  textEnable: {
    color: colors.grey800,
    fontSize: 12,
  },
  textDisclaimer: {
    fontSize: 10,
    color: colors.grey800,
  },
  relatedSection: {
    marginBlock: '.5rem',
  },
  relatedLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: colors.grey600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(0.5),
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '.5rem',
  },
  relatedChipLabel: {
    display: 'inline-block',
    fontSize: '12px',
    color: colors.grey800,
    maxWidth: '15ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  relatedChip: {
    '& .MuiAvatar-root': {
      width: 24,
      height: 24,
      // borderRadius: '.25rem',
    },
  },
  popover: {
    pointerEvents: 'none',
  },
  popoverPaper: {
    padding: '.5rem',
    borderRadius: '.75rem',
    backdropFilter: 'blur(1rem)',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    boxShadow: '0 4px 1.5rem rgba(0, 0, 0, 0.1)',
  },
  popoverChipsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
}));

type RelatedItem = {
  id: string,
  name: string,
  imageUrl: string,
  type?: string,
};

type RelatedItemsChipsProps = {
  items: RelatedItem[],
  label: string,
};

function RelatedItemsChips({items, label}: RelatedItemsChipsProps) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const popoverOpen = Boolean(anchorEl);

  if (!items || items.length === 0) return null;

  return (
    <div className={classes.relatedSection}>
      <div className={classes.relatedLabel}>{label}</div>
      <div className={classes.chipsContainer}>
        {items.slice(0, 2).map((item, index) => (
          <CustomChip
            key={item.id || index}
            color="default"
            size="small"
            label={
              <span className={classes.relatedChipLabel}>{item.name}</span>
            }
            avatar={<Avatar src={item.imageUrl}>{item.name[0]}</Avatar>}
            className={classes.relatedChip}
          />
        ))}
        {items.length > 2 && (
          <>
            <CustomChip
              color="default"
              size="small"
              label={`+${items.length - 2} más`}
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              aria-owns={popoverOpen ? `${label}-popover` : undefined}
              aria-haspopup="true"
            />
            <Popover
              id={`${label}-popover`}
              className={classes.popover}
              classes={{
                paper: classes.popoverPaper,
              }}
              open={popoverOpen}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              onClose={handlePopoverClose}
              disableRestoreFocus
            >
              <div className={classes.popoverChipsContainer}>
                {items.slice(2).map((item, index) => (
                  <CustomChip
                    key={item.id || index}
                    color="default"
                    size="small"
                    label={
                      <span className={classes.relatedChipLabel}>
                        {item.name}
                      </span>
                    }
                    avatar={<Avatar src={item.imageUrl}>{item.name[0]}</Avatar>}
                    className={classes.relatedChip}
                  />
                ))}
              </div>
            </Popover>
          </>
        )}
      </div>
    </div>
  );
}

export default function PromotionCard(props: Props) {
  const {
    store,
    promotion,
    shouldCreatePromotions,
    type,
    handleClick,
    currency,
    cleanPromotion,
    updatePromotion,
  } = props;

  const hasStorePromotionFeature = useIsFeatureInTier('STORE_PROMOTION');

  const [openModal, setOpenModal] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const classes = useStyles();

  const promotionLink = useMemo(() => {
    if (!store || !promotion || !promotion.slug) return '';
    return `https://${getCustomDomain(store)}/promo/${
      promotion.slug ? promotion.slug : ''
    }`;
  }, [promotion, store]);

  useEffect(() => {
    if (promotion) {
      setEnabled(promotion.enabled);
    }
  }, [promotion]);

  const handleEnablePromotion = e => {
    if (promotion && updatePromotion) {
      if (promotion.enabled || shouldCreatePromotions) {
        const promotionRequest: any = {
          enabled: e.target.checked,
          mode: promotion.promotionMode,
        };

        if (Array.isArray(promotion.products) && promotion.products.length) {
          promotionRequest.products = promotion.products.map(product => ({
            productId: product.product.id,
          }));
        }

        if (
          Array.isArray(promotion.productCategories) &&
          promotion.productCategories.length
        ) {
          promotionRequest.categories = promotion.productCategories.map(
            productCategory => ({
              categoryId: productCategory.productCategory.id,
            })
          );
        }

        updatePromotion({
          promotionRequest,
          promotionId: promotion ? promotion.id : '',
        });
      } else if (!shouldCreatePromotions) {
        setOpenModal(true);
      }
    }
  };

  const hasCompleteImages =
    promotion && promotion.promotionImageId && promotion.promotionSmallImageId;

  if (type === 'CREATE') {
    return (
      <Card classes={{root: classes.cardRoot}}>
        {handleClick && (
          <CardActionArea
            className={classes.cardActionArea}
            onClick={() => {
              cleanPromotion();
              handleClick();
            }}
          >
            <div className={classes.empty}>
              <AddCircleTwoTone color="primary" fontSize="large" />
              <Typography className={classes.paragraph}>
                Crear una promoción nueva
              </Typography>
            </div>
          </CardActionArea>
        )}
      </Card>
    );
  }

  if (!promotion || !currency) return null;

  return (
    <Card classes={{root: classes.root}}>
      <div className={classes.titleWrap}>
        <Typography className={classes.title} paragraph>
          {promotion.name}
        </Typography>

        <span
          className={`${classes.chip} 
        ${enabled ? '' : classes.chipExpired}
      `}
        >
          {enabled ? 'Activo' : 'Inactivo'}
        </span>
      </div>
      <div style={{height: '100%'}}>
        <ul className={classes.promotionInformation}>
          {promotion.promotionType === 'BUYANDPAY' &&
            typeof promotion.buyQuantity === 'number' &&
            typeof promotion.payQuantity === 'number' && (
              <li className={classes.description}>
                <span>{`${promotion.buyQuantity}x${promotion.payQuantity} en ${
                  promotion.promotionMode === 'ALL'
                    ? 'todos los productos.'
                    : promotion.promotionMode === 'PRODUCT'
                    ? 'productos seleccionados.'
                    : 'productos de categorias específicas.'
                }`}</span>
              </li>
            )}
          {promotion.promotionType === 'PERCENTAGE' &&
            promotion.discountPercentage && (
              <li className={classes.description}>
                <span>{`${Number(
                  promotion.discountPercentage
                )}% de descuento en ${
                  promotion.promotionMode === 'ALL'
                    ? 'todos los productos.'
                    : promotion.promotionMode === 'PRODUCT'
                    ? 'productos seleccionados.'
                    : 'productos de categorias específicas.'
                }`}</span>
              </li>
            )}
          {promotion.enabledOnOfferPrice ? (
            <li className={classes.description}>
              <span>Aplica sobre precios en oferta y al por mayor.</span>
            </li>
          ) : (
            <li className={classes.description}>
              <span>No aplica sobre precios de oferta ni al por mayor</span>
            </li>
          )}
          {promotion.enabledInteractWithOtherPromotions ? (
            <li className={classes.description}>
              <span>
                De haber dos a más promociones, se aplica la de mayor descuento
                para el cliente.
              </span>
            </li>
          ) : (
            <li className={classes.description}>
              <span>No aplica en conjunto con otras promociones.</span>
            </li>
          )}

          {typeof promotion.minQuantity === 'number' && (
            <li className={classes.description}>
              <span>{`Requerimiento mínimo: mínimo ${promotion.minQuantity} productos`}</span>
            </li>
          )}

          {promotion.minAmount &&
            (promotion.promotionType !== 'BUYANDPAY' ? (
              <li className={classes.description}>
                <span>{`Requerimiento mínimo: compras mayores a ${commaFormat(
                  promotion.minAmount,
                  currency ? currency.symbol : ''
                )}`}</span>
              </li>
            ) : (
              <li className={classes.description}>
                <span>{`Aplicar sobre un monto mayor a ${commaFormat(
                  promotion.minAmount,
                  currency ? currency.symbol : ''
                )}.`}</span>
              </li>
            ))}

          {promotion.promotionType === 'BUYANDPAY' &&
            typeof promotion.maxUses === 'number' && (
              <li className={classes.description}>
                <span>{`Máximo de usos por orden: ${promotion.maxUses}`}</span>
              </li>
            )}
          {promotion.maxAmount && (
            <li className={classes.description}>
              <span>{`Monto máximo de descuento por orden ${commaFormat(
                promotion.maxAmount,
                currency ? currency.symbol : ''
              )}`}</span>
            </li>
          )}
        </ul>
      </div>

      {/* Related Products */}
      {promotion.products && promotion.products.length > 0 && (
        <RelatedItemsChips
          items={promotion.products.map(p => {
            const product = p.product;
            const imageUrl = product.mainImage
              ? getImageUrl(product.mainImage.id)
              : '';
            return {
              id: product.id,
              name: product.name,
              imageUrl,
              type: 'product',
            };
          })}
          label="Productos Relacionados"
        />
      )}

      {/* Related Categories */}
      {promotion.productCategories && promotion.productCategories.length > 0 && (
        <RelatedItemsChips
          items={promotion.productCategories.map(c => {
            const category = c.productCategory;
            const imageUrl =
              category.portraitImageId || category.landscapeImageId
                ? getImageUrl(
                    category.portraitImageId || category.landscapeImageId
                  )
                : '';
            return {
              id: category.id,
              name: category.name,
              imageUrl,
              type: 'category',
            };
          })}
          label="Categorías Relacionadas"
        />
      )}

      <div className={classes.switchContainer}>
        <FormControlLabel
          control={
            <IosSwitch
              disabled={!hasStorePromotionFeature}
              className={classes.switchContainer}
              checked={enabled}
              onChange={handleEnablePromotion}
            />
          }
          label={
            <div className={classes.textEnable}>
              {enabled ? 'Activada' : 'Desactivada'}
            </div>
          }
        />
        <div className={classes.subtitleText}>
          <CustomButton
            component={Link}
            onClick={() => cleanPromotion()}
            to={`/marketing/promotions/${promotion.id}`}
          >
            {hasStorePromotionFeature ? (
              <>
                <div className={classes.icon}>
                  <Edit />
                </div>
                Editar
              </>
            ) : (
              'Ver'
            )}
          </CustomButton>
        </div>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.buttonWrap}>
        <ClickAwayListener onClickAway={() => setLinkCopied(false)}>
          <Tooltip
            placement="right"
            arrow
            PopperProps={{
              disablePortal: true,
            }}
            onClose={() => setLinkCopied(false)}
            open={linkCopied}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            leaveDelay={300}
            title="¡Link copiado!"
          >
            <CustomButton
              className={classes.copyLinkButton}
              color="primary"
              disableElevation
              onClick={() => {
                const textCopied = promotionLink;
                store &&
                  navigator &&
                  navigator.clipboard &&
                  navigator.clipboard
                    .writeText(textCopied)
                    .then(() => {
                      setLinkCopied(true);
                    })
                    .catch(e => console.error(e));
              }}
              startIcon={<FileCopyIcon color="primary" />}
            >
              Copiar link de promoción
            </CustomButton>
          </Tooltip>
        </ClickAwayListener>
        {!hasCompleteImages && promotion.enabled && (
          <Typography className={classes.textDisclaimer}>
            * Para visualizar promoción en tienda añadir imágenes.
          </Typography>
        )}
        <AlertActivePromotion
          openModal={openModal}
          handleConfirm={() => {
            if (promotion && updatePromotion) {
              const promotionRequest = {enabled: !promotion.enabled};
              updatePromotion({
                promotionRequest,
                promotionId: promotion ? promotion.id : '',
              });
              setOpenModal(false);
            }
          }}
          handleCloseModal={() => setOpenModal(false)}
        />
      </div>
    </Card>
  );
}
